import { useState } from "react";
import { NE_STATES } from "@/lib/data";
import { MapPin } from "lucide-react";

const STATE_POS = {
  SK: { x: 200, y: 200, label: "Sikkim" },
  AR: { x: 460, y: 150, label: "Arunachal" },
  AS: { x: 360, y: 240, label: "Assam" },
  ML: { x: 310, y: 295, label: "Meghalaya" },
  NL: { x: 450, y: 240, label: "Nagaland" },
  MN: { x: 465, y: 305, label: "Manipur" },
  MZ: { x: 425, y: 360, label: "Mizoram" },
  TR: { x: 365, y: 360, label: "Tripura" },
};

export default function Coverage() {
  const [active, setActive] = useState("TR");
  const activeState = NE_STATES.find((s) => s.code === active);

  return (
    <section
      id="coverage"
      data-testid="coverage-section"
      className="border-b border-damita-border"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-16 md:py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-6 lg:gap-12 mb-10 md:mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-damita-ink2 mb-6 flex items-center gap-3">
              <span className="w-8 tick-line" /> Sales &amp; Solutions in the Northeast
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
              Local hands. <br /> Local stock. <br /> Eight states.
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-5 text-damita-ink2 text-base md:text-lg lg:pt-8">
            We proudly support and power infrastructure across the entire Northeast,
            including Sikkim — turning difficult logistics into routine deliveries.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-px bg-damita-border border border-damita-border">
          {/* Schematic */}
          <div className="col-span-12 lg:col-span-7 bg-damita-surface relative overflow-hidden min-h-[420px] md:min-h-[520px]">
            <div className="absolute top-5 left-5 right-5 flex items-center justify-between z-10">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2">
                Coverage Schematic / 2025
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 hidden sm:inline">
                North-East · India
              </span>
            </div>

            <svg viewBox="0 0 600 520" className="absolute inset-0 w-full h-full p-6 md:p-10">
              <defs>
                <pattern id="grid2" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e4e4e7" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="600" height="520" fill="url(#grid2)" />

              {/* Northeast outline (simplified) */}
              <path
                d="M 175 180 L 215 175 L 240 195 L 270 215 L 300 220 L 340 200 L 395 175 L 440 140 L 495 125 L 510 160 L 480 195 L 495 220 L 505 255 L 480 285 L 490 320 L 470 360 L 445 380 L 415 385 L 380 395 L 350 380 L 320 370 L 295 340 L 280 305 L 255 280 L 230 250 L 200 230 L 180 210 Z"
                fill="#ffffff"
                stroke="#0A0A0A"
                strokeWidth="1.5"
              />

              {Object.keys(STATE_POS).map((code) => {
                const p = STATE_POS[code];
                return (
                  <line
                    key={`l-${code}`}
                    x1="340"
                    y1="290"
                    x2={p.x}
                    y2={p.y}
                    stroke={active === code ? "#002FA7" : "#d4d4d8"}
                    strokeWidth={active === code ? 1.2 : 0.4}
                    opacity={active === code ? 0.6 : 0.4}
                  />
                );
              })}

              {Object.entries(STATE_POS).map(([code, p]) => {
                const isActive = active === code;
                return (
                  <g key={code} style={{ cursor: "pointer" }} onClick={() => setActive(code)}>
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isActive ? 9 : 5}
                      fill={isActive ? "#FFD60A" : "#002FA7"}
                      stroke={isActive ? "#0A0A0A" : "#ffffff"}
                      strokeWidth={isActive ? 2 : 1.5}
                    />
                    {isActive && (
                      <circle cx={p.x} cy={p.y} r="14" fill="none" stroke="#002FA7" strokeWidth="1" opacity="0.5" />
                    )}
                    <text
                      x={p.x + 12}
                      y={p.y + 4}
                      fontSize="11"
                      fontFamily="IBM Plex Mono, monospace"
                      fontWeight={isActive ? 700 : 500}
                      fill={isActive ? "#0A0A0A" : "#52525B"}
                      style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                      {code}
                    </text>
                  </g>
                );
              })}
            </svg>

            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4 z-10">
              <div className="bg-white border border-damita-ink p-3 md:p-4 max-w-xs">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-1">Selected</div>
                <div className="font-display font-bold text-lg md:text-xl tracking-tight flex items-center gap-2">
                  <MapPin size={16} className="text-damita-blue" />
                  {activeState?.name}
                </div>
              </div>
              <div className="hidden md:flex flex-col items-end gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-damita-blue" /> Active Zone</span>
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-damita-yellow" /> Selected</span>
              </div>
            </div>
          </div>

          {/* States grid */}
          <div className="col-span-12 lg:col-span-5 bg-white p-6 md:p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-damita-ink2 mb-4">
              All 8 Sister States
            </div>
            <div className="grid grid-cols-2 gap-px bg-damita-border border border-damita-border">
              {NE_STATES.map((s) => (
                <button
                  key={s.code}
                  data-testid={`state-${s.code}`}
                  onClick={() => setActive(s.code)}
                  className={`text-left px-4 py-4 transition-colors ${
                    active === s.code ? "bg-damita-blue text-white" : "bg-white hover:bg-damita-surface"
                  }`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-70">/ {s.code}</div>
                  <div className="font-display font-bold text-base md:text-lg tracking-tight">{s.name}</div>
                </button>
              ))}
            </div>
            <p className="mt-6 text-sm text-damita-ink2">
              Plus regional offices in West Bengal, Bihar, Odisha &amp; Jharkhand.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
